import { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Send, 
  Sparkles, 
  Moon, 
  Star,
  Loader2,
  User,
  Bot,
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  RefreshCw,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useUI } from '@/contexts/UIContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vedvaani`;

export default function VedVaaniChat({ isOpen, onClose, user }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [showBirthForm, setShowBirthForm] = useState(false);
  const [birthDetails, setBirthDetails] = useState({
    dob: '',
    tob: '',
    pob: ''
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { setHideBottomNav } = useUI();

  // Hide bottom nav when chat opens, show it when it closes
  useEffect(() => {
    setHideBottomNav(isOpen);
    return () => setHideBottomNav(false);
  }, [isOpen, setHideBottomNav]);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      // Add welcome message
      if (messages.length === 0) {
        setMessages([{
          role: 'assistant',
          content: `🙏 **Hari Om! Namaste!**

I am **VedVaani** (वेदवाणी), your AI Jyotish Guide on DharmBook.

I can help you with:
- ✨ Understanding your Kundali and planetary positions
- 🌙 Explaining Rashis, Nakshatras, and Doshas
- 🕉️ Providing spiritual remedies (mantras, donations, fasting)
- 🌟 General life guidance based on Vedic astrology

**How can I assist you today?**

_For personalized guidance, you may share your birth details (date, time, place) when asked._`,
          timestamp: new Date().toISOString()
        }]);
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/status?token=${token}`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(`${API}/chat?token=${token}`, {
        message: userMessage,
        session_id: sessionId,
        birth_details: birthDetails.dob ? birthDetails : null
      });
      
      const data = response.data;
      setSessionId(data.session_id);
      setStatus(prev => ({ ...prev, remaining: data.remaining_messages }));
      
      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString()
      }]);
      
      // Show birth details form if needed
      if (data.birth_details_needed && !birthDetails.dob) {
        setShowBirthForm(true);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get response. Please try again.';
      
      if (error.response?.status === 429) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `🙏 आपकी दैनिक सीमा समाप्त हो गई है।

You have reached your daily message limit. Please:
- Try again tomorrow for more free guidance
- Consult our expert human astrologers for immediate assistance

धन्यवाद for using VedVaani! 🙏`,
          timestamp: new Date().toISOString()
        }]);
      } else if (error.response?.status === 503) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `🙏 VedVaani is currently under maintenance.

Please try again later or consult our human astrologers.

धन्यवाद! 🙏`,
          timestamp: new Date().toISOString()
        }]);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const saveBirthDetails = () => {
    setShowBirthForm(false);
    if (birthDetails.dob) {
      toast.success('Birth details saved! VedVaani will use this for personalized guidance.');
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `🙏 **Chat cleared!**

How can I help you with Vedic astrology today?`,
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
  };

  const suggestedQuestions = [
    "What is my Rashi?",
    "Explain Manglik dosha",
    "Remedies for Sade Sati",
    "Which gemstone suits me?",
    "What is Mahadasha?"
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-purple-950 to-indigo-950 overflow-hidden">
      {/* Animated Stars Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 3 + 's',
              animationDuration: Math.random() * 3 + 2 + 's',
              opacity: Math.random() * 0.5 + 0.3
            }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="relative bg-gradient-to-r from-purple-900/80 to-indigo-900/80 backdrop-blur-sm border-b border-amber-500/30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-amber-300" style={{ fontFamily: 'Merriweather, serif' }}>
                वेदवाणी
              </h1>
              <p className="text-xs text-purple-300">VedVaani - AI Jyotish Guide</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {status && (
              <div className="text-xs text-purple-300 bg-purple-800/50 px-3 py-1.5 rounded-full">
                {status.remaining !== undefined ? (
                  <span>{status.remaining} messages left today</span>
                ) : (
                  <span>Daily limit: {status.daily_limit}</span>
                )}
              </div>
            )}
            <button
              onClick={clearChat}
              className="p-2 text-purple-300 hover:text-white hover:bg-purple-800/50 rounded-full transition-colors"
              title="Clear chat"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-purple-300 hover:text-white hover:bg-purple-800/50 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto h-[calc(100vh-200px)] md:h-[calc(100vh-180px)] overflow-y-auto px-4 py-6 space-y-4 scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-transparent">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              msg.role === 'user' 
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                : 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/30'
            }`}>
              {msg.role === 'user' ? (
                <User className="w-5 h-5 text-white" />
              ) : (
                <Sparkles className="w-5 h-5 text-white" />
              )}
            </div>
            
            <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
              <div className={`rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                  : 'bg-purple-900/60 backdrop-blur-sm border border-amber-500/20 text-purple-100'
              }`}>
                {msg.role === 'assistant' ? (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="text-amber-300">{children}</strong>,
                        em: ({ children }) => <em className="text-purple-300">{children}</em>,
                        ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>,
                        li: ({ children }) => <li className="text-purple-200">{children}</li>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
              <p className="text-xs text-purple-400 mt-1 px-2">
                {new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="bg-purple-900/60 backdrop-blur-sm border border-amber-500/20 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 text-amber-300">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">VedVaani is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions (show only at start) */}
      {messages.length <= 1 && (
        <div className="max-w-4xl mx-auto px-4 pb-4">
          <p className="text-xs text-purple-400 mb-2">Suggested questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setInputValue(q)}
                className="text-xs px-3 py-1.5 bg-purple-800/50 text-purple-300 rounded-full hover:bg-purple-700/50 hover:text-amber-300 transition-colors border border-purple-600/30"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Birth Details Form Modal */}
      {showBirthForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl max-w-md w-full p-6 border border-amber-500/30 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-amber-300">Share Birth Details</h3>
                <p className="text-xs text-purple-300">For personalized guidance</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-purple-300 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date of Birth
                </label>
                <input
                  type="date"
                  value={birthDetails.dob}
                  onChange={(e) => setBirthDetails(prev => ({ ...prev, dob: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-purple-800/50 border border-purple-600/50 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm text-purple-300 mb-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Time of Birth (optional)
                </label>
                <input
                  type="time"
                  value={birthDetails.tob}
                  onChange={(e) => setBirthDetails(prev => ({ ...prev, tob: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-purple-800/50 border border-purple-600/50 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm text-purple-300 mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Place of Birth (optional)
                </label>
                <input
                  type="text"
                  value={birthDetails.pob}
                  onChange={(e) => setBirthDetails(prev => ({ ...prev, pob: e.target.value }))}
                  placeholder="e.g., Delhi, India"
                  className="w-full px-4 py-2.5 bg-purple-800/50 border border-purple-600/50 rounded-xl text-white placeholder-purple-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowBirthForm(false)}
                className="flex-1 py-2.5 border border-purple-500/50 text-purple-300 rounded-xl hover:bg-purple-800/50 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={saveBirthDetails}
                className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all"
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent pt-8 pb-6 px-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto">
          {/* Disclaimer */}
          <p className="text-[10px] text-purple-400/70 text-center mb-2 flex items-center justify-center gap-1">
            <AlertCircle className="w-3 h-3" />
            AI guidance is for spiritual insight only. Consult human astrologers for detailed readings.
          </p>
          
          <div className="flex gap-3 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask VedVaani about astrology..."
                rows={1}
                className="w-full px-4 py-3 bg-purple-900/60 backdrop-blur-sm border border-amber-500/30 rounded-2xl text-white placeholder-purple-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                style={{ minHeight: '48px', maxHeight: '120px' }}
                disabled={loading || (status?.remaining === 0)}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || loading || (status?.remaining === 0)}
              className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:shadow-lg hover:shadow-amber-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .safe-area-bottom {
          padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 1.5rem));
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.5);
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.7);
        }
      `}</style>
    </div>
  );
}
